import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  listRatePlans,
  listWorkspaceRatePlans,
  setWorkspaceBillingCurrency,
  setWorkspaceBillingTerm,
  type WorkspacePinRow,
} from './api';
import { onlyDate } from './money';

/**
 * What each workspace is paying, and on which price list.
 *
 * This lives on the Workspaces tab rather than under Pricing, because the
 * question it answers is about a customer, not about a price: "what is this
 * one on, and can I leave them there?" It sat at the bottom of the pricing
 * screen, three scrolls below anything to do with it, while the rest of
 * per-customer administration lived here.
 *
 * The pin is the whole grandfathering mechanism: a workspace pays the list it
 * is pinned to until something deliberately moves it, and a pin marked
 * "negotiated" is a contract -- bulk migrations step around it.
 */
export default function WorkspacePricing({ toast }: { toast: (message: string) => void }) {
  const [rows, setRows] = useState<WorkspacePinRow[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pins, plans] = await Promise.all([listWorkspaceRatePlans(), listRatePlans()]);
      setRows(pins);
      setCurrencies([...new Set(plans.filter((p) => p.status === 'published').map((p) => p.currency))].sort());
    } catch (e) {
      toast(`Could not list what workspaces are paying: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name ?? '').toLowerCase().includes(q)
      || (r.edition_key ?? '').toLowerCase().includes(q)
      || r.pin_reason.toLowerCase().includes(q)
      || r.currency.toLowerCase().includes(q));
  }, [rows, query]);

  const negotiated = rows.filter((r) => r.pin_reason === 'negotiated').length;

  const act = async (label: string, fn: () => Promise<void>) => {
    try {
      await fn();
      await load();
      toast(label);
    } catch (e) {
      toast(`Refused: ${(e as Error).message}`);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>What workspaces are paying</h2>
          <p className="sub" style={{ margin: '3px 0 0' }}>
            Which price list each workspace is on, whether it bills monthly or yearly, and in what
            currency. A workspace stays on its list until something deliberately moves it.
          </p>
        </div>
        <button className="btn" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Show'}
        </button>
      </div>

      {!open ? null : loading ? (
        <p className="muted" style={{ marginTop: 14 }}>Loading…</p>
      ) : (
        <>
          <div className="row" style={{ marginTop: 14, gap: 12, flexWrap: 'wrap' }}>
            <input
              value={query}
              placeholder="Find a workspace"
              onChange={(e) => setQuery(e.target.value)}
              style={{ maxWidth: 300 }}
            />
            <span className="muted" style={{ fontSize: 12.5 }}>
              {rows.length} workspace{rows.length === 1 ? '' : 's'}
              {negotiated ? ` · ${negotiated} on a negotiated deal` : ''}
            </span>
          </div>

          <div className="matrix-scroll" style={{ marginTop: 12, maxHeight: 420, overflow: 'auto' }}>
            <table className="matrix">
              <thead>
                <tr>
                  <th>Workspace</th><th>Plan</th><th>On price list</th><th>Why</th>
                  <th>Billed</th><th>Currency</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.account_id}>
                    <td className="feat-name">{r.name ?? r.account_id.slice(0, 8)}</td>
                    <td>{r.edition_key ?? <span className="muted">none</span>}</td>
                    <td>
                      Version {r.version} <span className="muted">{r.currency}</span>
                      {r.status !== 'published' ? (
                        <span className="pill" style={{ marginLeft: 6 }}>
                          {r.status === 'archived' ? 'retired' : r.status}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {r.pin_reason === 'negotiated' ? (
                        <span style={{ color: 'var(--on)', fontWeight: 600 }}>negotiated deal</span>
                      ) : (
                        <span className="muted">{r.pin_reason}</span>
                      )}
                      <div className="muted" style={{ fontSize: 11 }}>since {onlyDate(r.pinned_at)}</div>
                    </td>
                    <td>
                      <select
                        value={r.billing_term}
                        style={{ padding: '4px 6px', fontSize: 12 }}
                        onChange={(e) =>
                          act(`${r.name ?? 'Workspace'} now bills ${e.target.value}`, () =>
                            setWorkspaceBillingTerm(r.account_id, e.target.value as 'monthly' | 'annual'))
                        }
                      >
                        <option value="monthly">monthly</option>
                        <option value="annual">yearly</option>
                      </select>
                    </td>
                    <td>
                      <select
                        value={r.currency}
                        style={{ padding: '4px 6px', fontSize: 12 }}
                        onChange={(e) =>
                          act(`${r.name ?? 'Workspace'} moved to ${e.target.value}`, () =>
                            setWorkspaceBillingCurrency(r.account_id, e.target.value))
                        }
                      >
                        {[...new Set([r.currency, ...currencies])].map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="hint" style={{ marginTop: 10 }}>
            Changing a workspace&rsquo;s currency moves it onto the live list for that currency —
            there is no old list in the new currency for it to be grandfathered on. Switching to
            yearly is refused if its current list has no yearly price for its plan.
          </p>
        </>
      )}
    </div>
  );
}
